import React, { useState, useEffect, useRef, Suspense } from "react";
import "./SourceDetail.scss";
import SourceImage from "../SourceImage/SourceImage";
import Table from "../Table/Table";
import topImage from "../../resources/images/placeholder-4.png";
import { formatData, isEmptyString } from "../../helperFunc";
import { _ } from "gridjs-react";
// import Plot from 'react-plotly.js';
import Plotly from "plotly.js-basic-dist-min";
import createPlotlyComponent from "react-plotly.js/factory";
import { CSVLink } from "react-csv";
import { FiDownload } from "react-icons/fi";
import { getPlotAxis } from "../../helperFunc";

const UNDESIRED_COLUMNS = [
  "id",
  "metadata_id",
  "source_name",
  "source_id",
  "pointing_name",
];

const SOURCE_INFO_GROUPS = {
  group1: [
    "ra_mean",
    "ra_mean_e",
    "ra_max",
    "ra_max_e",
    "dec_mean",
    "dec_mean_e",
    "dec_max",
    "dec_max_e",
  ],
  group2: [
    "total_flux",
    "total_flux_e",
    "total_flux_e_sys",
    "total_flux_measured",
    "total_flux_measured_e",
  ],
  group3: ["distance_pointing", "distance_nn", "s_code", "n_gauss"],
  group4: [
    "isl_total_flux",
    "isl_total_flux_e",
    "isl_rms",
    "isl_mean",
    "resid_isl_rms",
    "resid_isl_mean",
    "flux_correction",
  ],
  group5: ["real_source", "resolved", "source_linked"],
};
const Plot = createPlotlyComponent(Plotly);

function SourceDetail({
  data,
  columnDesc,
  artefactsData,
  isArtefactsRequestLoading,
  catalogueMetadataId,
  sourceId,
  getSpectralPlot
}) {
  const [filterMap, setFilterMap] = useState({});
  const [selectedSpectralWindow, setSelectedSpectralWindow] = useState("");
  const [selectedBand, setSelectedBand] = useState("");
  const [imageArray, setImageArray] = useState([]);
  const [sourceData, setSourceData] = useState({});
  const [imagesWithLabels, setImagesWithLabels] = useState({});
  const [currentSourceId, setCurrentSourceId] = useState(sourceId);
  const [plotDisplayData, setPlotDisplayData] = useState();

  const gaussColumns = useRef([]);
  const spectralColumns = useRef([]);
  const sourceSpectraColumns = useRef([]);

  let {data:spectralPlotData} = getSpectralPlot({
      sourceId: currentSourceId,
      catalogueId:catalogueMetadataId
  });

  let plotAxis = getPlotAxis(spectralPlotData?.data);

  useEffect(() => {
    plotAxis = getPlotAxis(spectralPlotData?.data);
    let xyPlotData =
      spectralPlotData?.data?.reduce(
        (acc, curr) => {
          acc["x"] = [...acc["x"], curr[plotAxis.X.header]];
          acc["y"] = [...acc["y"], curr[plotAxis.Y.header]];
          return acc;
        },
        { x: [], y: [] }
      ) || [];
    setPlotDisplayData(xyPlotData);
  }, [spectralPlotData]);

  useEffect(() => {
    if (!isArtefactsRequestLoading && artefactsData) {
      let { artefacts, display_order, reference_spw, reference_freq } = artefactsData;
      let otherImages = [];
      display_order.forEach((spw) => {
        const ref_freq =
          reference_freq[spw] !== undefined
            ? reference_freq[spw].toFixed(2)
            : "NA";
        const image = {
          src: artefacts[spw].cutout_png,
          alt: "",
          label: `${spw}: ${ref_freq}`,
          fits: artefacts[spw].cutout_fits,
        };
        otherImages.push(image);
        setImagesWithLabels((prevImageData) => {
          return {
            ...prevImageData,
            [spw]: image,
          };
        });
      });

      setImageArray(otherImages);
    }
  }, [artefactsData, isArtefactsRequestLoading]);

  function updateCurrentSourceId(source) {
    if(source && source?.length == 1) {
      setCurrentSourceId(source[0].id);
    }
  }

  function createGaussSpectralAndSpectraColumns(
    gauss,
    spectral,
    source_spectra
  ) {
    gaussColumns.current = gauss[0]
      ? [
          ...Object.keys(gauss[0]).map((key) => {
            return {
              id: key,
              name: _(
                <>
                  <span>{key.replaceAll("_", " ")}</span>
                  <br />
                  {columnDesc[key] && !isEmptyString(columnDesc[key].unit) ? (
                    <span className="table-unit">({columnDesc[key].unit})</span>
                  ) : (
                    <></>
                  )}
                </>
              ),
              formatter: (cell) => `${formatData(key, cell)}`,
              hidden: UNDESIRED_COLUMNS.includes(key),
            };
          }),
        ]
      : [];

    spectralColumns.current = spectral[0]
      ? [
          ...Object.keys(spectral[0]).map((key) => {
            return {
              id: key,
              name: key.replaceAll("_", " "),
              formatter: (cell) => `${formatData(key, cell)}`,
              hidden: UNDESIRED_COLUMNS.includes(key),
            };
          }),
        ]
      : [];

    sourceSpectraColumns.current = source_spectra[0]
      ? [
          ...Object.keys(source_spectra[0]).map((key) => {
            return {
              id: key,
              name: key.replaceAll("_", " "),
              formatter: (cell) => `${formatData(key, cell)}`,
              hidden: UNDESIRED_COLUMNS.includes(key),
            };
          }),
        ]
      : [];
  }

  useEffect(() => {
    setSourceData(data);
    const map = {};
    Object.keys(data || {}).forEach((key, i) => {
      const subKeys = Object.keys(data[key]);
      if (i === 0) {
        setSelectedBand(key);
        setSelectedSpectralWindow(subKeys[0]);
      }
      map[key] = subKeys;
    });
    setFilterMap(map);
    const { gauss, source, spectral, source_spectra } = Object.values(
      Object.values(data)[0]
    )[0];
    createGaussSpectralAndSpectraColumns(gauss, spectral, source_spectra);
    updateCurrentSourceId(source);
    

  }, [data]);

  function columnName(field) {
    let text = field.replaceAll("_", " "),
      unit = "";
    if (field in columnDesc) {
      text = columnDesc[field].text || text;

      if (
        "unit" in columnDesc[field] &&
        !isEmptyString(columnDesc[field]["unit"])
      ) {
        unit = `(${columnDesc[field].unit})`;
      }
    }

    return (
      <span>
        <b>{text}</b>
        <span className="column-unit">{unit}</span>
      </span>
    );
  }

  function handleBandChange(e) {
    const { value } = e.target;
    setSelectedBand(value);
    setSelectedSpectralWindow(filterMap[value][0]);
    const { gauss, source, spectral, source_spectra } =
          data[value][filterMap[value][0]];

    createGaussSpectralAndSpectraColumns(gauss, spectral, source_spectra);
    updateCurrentSourceId(source);    
  }

  function handleSPWChange(e) {
    const { value } = e.target;
    setSelectedSpectralWindow(value);
    const { gauss, source, spectral, source_spectra } = data[selectedBand][value];
    createGaussSpectralAndSpectraColumns(gauss, spectral, source_spectra);
    updateCurrentSourceId(source);
  }

  return (
    <>
      <div className="source-detail">
        <div className="data-filter">
          <div className="filters">
            <label>
              <span>Observation Band</span>
              <div className="select-wrapper">
                <select
                  name="band"
                  onChange={handleBandChange}
                  value={selectedBand}
                >
                  {Object.keys(filterMap).map((value) => (
                    <option value={value} key={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
            </label>
            <label>
              <span>Spectral Window</span>
              <div className="select-wrapper">
                <select
                  name="spectral-window"
                  onChange={handleSPWChange}
                  value={selectedSpectralWindow}
                  data-testid="spwDropdown"
                >
                  {filterMap[selectedBand]?.map((value) => (
                    <option value={value} key={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
            </label>
          </div>
        </div>
        <div className="left-pane" data-testid="left-panel">
          <SourceImage
            data={
              selectedBand &&
              selectedSpectralWindow &&
              imagesWithLabels[selectedSpectralWindow]
                ? imagesWithLabels[selectedSpectralWindow]
                : { src: topImage, alt: "" }
            }
          />
          <div className="metadata">
            {selectedBand && selectedSpectralWindow ? (
              <>
                <h3>
                  {
                    sourceData[selectedBand][selectedSpectralWindow].metadata[0]
                      .pointing_name
                  }
                </h3>
                {Object.keys(
                  sourceData[selectedBand][selectedSpectralWindow].metadata[0]
                )
                  .filter((el) => !UNDESIRED_COLUMNS.includes(el))
                  .map((field) => {
                    return (
                      <p key={field}>
                        <span className="metadata-key">
                          {columnName(field)}
                        </span>
                        <span className="metadata-value">
                          {formatData(
                            field,
                            sourceData[selectedBand][selectedSpectralWindow]
                              .metadata[0][field]
                          )}
                        </span>
                      </p>
                    );
                  })}
              </>
            ) : (
              "Loading content"
            )}
          </div>
        </div>
        <div className="right-pane">
          <div className="source-info">
            {selectedBand && selectedSpectralWindow ? (
              <>
                <h3>
                  {
                    sourceData[selectedBand][selectedSpectralWindow].source[0]
                      .source_name
                  }
                </h3>
                <div className="body">
                  {Object.values(SOURCE_INFO_GROUPS).map((groups, i) => {
                    return (
                      <div className="info-group" key={i}>
                        {groups.map((field) => {
                          return (
                            <p key={field}>
                              {columnName(field)}
                              {formatData(
                                field,
                                sourceData[selectedBand][selectedSpectralWindow]
                                  .source[0][field]
                              )}
                            </p>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              "Loading content"
            )}
          </div>
          <section className="gauss">
            <h3>Gaussian Decomposition</h3>
            <div className="body">
              {selectedBand && selectedSpectralWindow ? (
                <Table
                  columns={gaussColumns.current}
                  data={sourceData[selectedBand][selectedSpectralWindow].gauss}
                  pagination={{
                    enabled: true,
                    limit: 10,
                  }}
                  fixedHeader={true}
                  sort={true}
                  resizeableColumns={true}
                />
              ) : (
                "Loading content"
              )}
            </div>
          </section>
          <div className="plot-placeholder">Plot</div>
          <section className="spectral">
            <h3>Spectral Indices</h3>
            <div className="body">
              {selectedBand && selectedSpectralWindow ? (
                <Table
                  columns={spectralColumns.current}
                  data={
                    sourceData[selectedBand][selectedSpectralWindow].spectral
                  }
                  pagination={{
                    enabled: true,
                    limit: 10,
                  }}
                  fixedHeader={true}
                  sort={true}
                  resizeableColumns={true}
                />
              ) : (
                "Loading content"
              )}
            </div>
          </section>

          {imageArray.length > 0 ? (
            <div className="images">
              {imageArray.map((img, idx) => (
                <SourceImage key={idx} data={img} />
              ))}
            </div>
          ) : (
            <></>
          )}

        <section className="spectra">
              <h3>Spectra</h3>
              <div className="body spectra-cont">
                {selectedBand && selectedSpectralWindow  ? (
                  <>
                    { sourceSpectraColumns.current.length !== 0 && spectralPlotData?.data &&  (
                      <div className="plot">
                        <CSVLink
                          data={spectralPlotData?.data}
                          filename={"spectralPlot" + `data.csv`}
                          className="border-green-bg-transparent-btn download"
                        >
                         Plot Data
                          <FiDownload
                            style={{ marginLeft: "9px", minWidth: "10px" }}
                          />
                        </CSVLink>
                        <Plot
                        useResizeHandler={true}
                        style={{width:'100%', height: '100%'}}
                          data={[
                            {
                              x: plotDisplayData.x,
                              y: plotDisplayData.y,
                              type: "scatter",
                            },
                          ]}
                          layout={{
                            // width: "1000px",
                            // height: "100%",
                            title: `Spectral Plot - ${spectralPlotData?.spw}`,
                            xaxis: {
                              title: {
                                text: plotAxis.X.label,
                                font: {
                                  family: 'Courier New, monospace',
                                  size: 18,
                                  color: '#7f7f7f'
                                }
                              },
                            },
                            yaxis: {
                              title: {
                                text: plotAxis.Y.label,
                                font: {
                                  family: 'Courier New, monospace',
                                  size: 18,
                                  color: '#7f7f7f'
                                }
                              },
                            },
                          }}
                        />
                      </div>
                    )}
                    <Table
                      columns={sourceSpectraColumns.current}
                      data={
                        sourceData[selectedBand][selectedSpectralWindow]
                          .source_spectra
                      }
                      pagination={{
                        enabled: true,
                        limit: 10,
                      }}
                      fixedHeader={true}
                      sort={true}
                      resizeableColumns={true}
                    />
                  </>
                ) : (
                  "Loading content"
                )}
              </div>
            </section>


        </div>
      </div>
    </>
  );
}

export default SourceDetail;
